// src/hooks/useMonthLayout.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { isLayoutBoxMeasurable } from './monthLayoutVisibility';

const REAL_EVENT_SELECTOR = '.event-box:not([data-ph="1"])';
const ANY_EVENT_SELECTOR = '.event-box';

/**
 * Hook do zarządzania layoutem miesiąca (skalowanie i kolumny)
 */
export function useMonthLayout(monthKey, date, events, columnAssignments, options = {}) {
    const { freeze = false } = options;
    const scaleBoxRef = useRef(null);
    const scaleInnerRef = useRef(null);
    const [layoutTick, setLayoutTick] = useState(0);

    const resetInlineSizing = useCallback(() => {
        const box = scaleBoxRef.current;
        const inner = scaleInnerRef.current;
        if (box) {
            box.style.height = '';
            box.style.minHeight = '';
        }
        if (inner) {
            inner.style.transform = '';
            inner.style.transformOrigin = 'left top';
        }
    }, []);

    const measureAndPublishMonthMetrics = useCallback(() => {
        if (freeze) return;
        const box = scaleBoxRef.current;
        const inner = scaleInnerRef.current;
        if (!box || !inner) return;
        if (!isLayoutBoxMeasurable(box)) return;

        // chwilowo bez skali, by pomiar był naturalny
        const prevTransform = inner.style.transform;
        inner.style.transform = 'none';

        // policz gap (z wrappera flex)
        const wrapper = inner.querySelector('.day-events-wrapper');
        const style = wrapper ? getComputedStyle(wrapper) : null;
        const gapStr = style && (style.columnGap || style.gap) ? (style.columnGap || style.gap) : '16px';
        const gapPx = parseFloat(gapStr) || 16;

        // zbierz REALNE kafle (bez placeholderów)
        const boxes = inner.querySelectorAll(REAL_EVENT_SELECTOR);
        let localCols = 0;
        let localMaxColW = 220;
        boxes.forEach((b) => {
            localCols += 1;
            const w = Math.max(b.scrollWidth, b.offsetWidth) || 220;
            if (w > localMaxColW) localMaxColW = w;
        });

        // zapis do globalnego store
        const store = (window.__planner_layout__ = window.__planner_layout__ || {});
        const m = (store[monthKey] = store[monthKey] || { maxCols: 0, colW: 220, gap: 16 });
        m.gap = Number.isFinite(gapPx) && gapPx > 0 ? gapPx : m.gap;
        if (localCols > m.maxCols) m.maxCols = localCols;
        if (localMaxColW > m.colW) m.colW = localMaxColW;

        // przywróć transform i powiadom
        inner.style.transform = prevTransform || '';
        try {
            window.dispatchEvent(new CustomEvent('planner:layoutMonthMetrics', { detail: { monthKey } }));
        } catch { }
    }, [freeze, monthKey]);

    const applyMonthLayout = useCallback(() => {
        if (freeze) {
            resetInlineSizing();
            return;
        }
        const box = scaleBoxRef.current;
        const inner = scaleInnerRef.current;
        if (!box || !inner) return;
        if (!isLayoutBoxMeasurable(box)) return;

        const store = window.__planner_layout__ || {};
        const m = store[monthKey];
        if (!m) return;

        const cols = Math.max(1, m.maxCols || 1);
        const gap = Math.max(0, Number(m.gap) || 16);

        const isMobile = typeof window !== 'undefined'
            ? window.matchMedia('(max-width: 768px)').matches
            : false;

        if (isMobile) {
            // MOBILE: autoscale całego rzędu (bez powiększania)
            const colW = Math.max(180, Math.round(m.colW));
            const nodes = inner.querySelectorAll(ANY_EVENT_SELECTOR);
            nodes.forEach((el) => {
                el.style.width = `${colW}px`;
                el.style.minWidth = `${colW}px`;
                el.style.flex = `0 0 ${colW}px`;
            });

            const need = (colW * cols) + (gap * Math.max(cols - 1, 0));
            const have = box.clientWidth || 1;

            let s = have / need;
            if (!Number.isFinite(s) || s <= 0) s = 1;
            if (s > 1) s = 1; // nie powiększamy na mobile

            inner.style.transform = `scale(${s})`;
            inner.style.transformOrigin = 'left top';

            // dopasuj wysokość wrappera do przeskalowanej zawartości
            const prev = inner.style.transform;
            inner.style.transform = 'none';
            const naturalH = inner.offsetHeight;
            inner.style.transform = prev;
            box.style.height = `${naturalH * s}px`;
        } else {
            // DESKTOP: bez skali, rozciągnij kolumny tak, by wypełnić wiersz
            const have = box.clientWidth || 1;
            let targetColW = Math.floor((have - gap * (cols - 1)) / cols);
            if (!Number.isFinite(targetColW) || targetColW <= 0) targetColW = 200;

            const nodes = inner.querySelectorAll(ANY_EVENT_SELECTOR);
            nodes.forEach((el) => {
                el.style.width = `${targetColW}px`;
                el.style.minWidth = `${targetColW}px`;
                el.style.flex = `0 0 ${targetColW}px`;
            });

            inner.style.transform = 'none';
            inner.style.transformOrigin = 'left top';
            box.style.height = '';
        }
    }, [freeze, monthKey, resetInlineSizing]);

    useEffect(() => {
        if (freeze) {
            resetInlineSizing();
            return;
        }

        const t = setTimeout(() => {
            measureAndPublishMonthMetrics();
            applyMonthLayout();
        }, 0);

        const onResize = () => applyMonthLayout();
        window.addEventListener('resize', onResize);

        const onMetrics = (e) => {
            if (e?.detail?.monthKey === monthKey) {
                setLayoutTick(v => v + 1);
                applyMonthLayout();
            }
        };
        window.addEventListener('planner:layoutMonthMetrics', onMetrics);

        return () => {
            clearTimeout(t);
            window.removeEventListener('resize', onResize);
            window.removeEventListener('planner:layoutMonthMetrics', onMetrics);
        };
    }, [date, events, columnAssignments, monthKey, freeze, applyMonthLayout, measureAndPublishMonthMetrics, resetInlineSizing]);

    const monthMetrics = (typeof window !== 'undefined' && window.__planner_layout__ && window.__planner_layout__[monthKey]) || null;
    const targetCols = monthMetrics?.maxCols || null;

    return { scaleBoxRef, scaleInnerRef, targetCols };
}

